import {
	AnalyticsDefinitionParam,
	AnalyticsGeneratedReport,
	AnalyticsReportDefinition,
} from "../../../../../services/analytics.service";

/**
 * Rules and copy for the saved-definition controls (SIMS-7954).
 *
 * Extracted from the tab component for the same reason `projection.ts` was: jest
 * excludes `index.tsx` from coverage, so validation and failure-classification
 * left inline would ship untested. The wording constants live here too, because
 * what the feedback control claims is a correctness property — see
 * FEEDBACK_DISCLAIMER — and a claim worth getting right is a claim worth
 * asserting in a test.
 */

/** A 24-character hex Mongo id, the OBJECT_ID slot shape. */
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

/** ISO-8601 calendar date. The backend accepts a date or a full timestamp. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

/**
 * What the feedback control is allowed to say.
 *
 * Under the Phase 3 design (SIMS-7811) a rating is a signal and nothing else:
 * promotion of a pattern into the shared knowledge base is a separate curated
 * step that happens through code review, deliberately not from the UI. Wording
 * such as "teach the assistant" or "improve future answers" would be a false
 * promise — the request path writes nothing to the knowledge base and nothing
 * about the model's behaviour changes. These strings state only what actually
 * happens.
 */
export const FEEDBACK_PROMPT = "Was this report useful?";
export const FEEDBACK_DISCLAIMER =
	"A quality signal only — it does not change the assistant's answers and adds nothing to the shared knowledge base.";
export const FEEDBACK_RECORDED = "Rating recorded. Nothing was published.";
export const FEEDBACK_FAILED = "Could not record that rating.";

/** Panel copy. Definitions are per-user by design, so the list says so. */
export const DEFINITIONS_PRIVACY_NOTE = "Only you can see these.";
export const DEFINITIONS_EMPTY =
	"No saved reports yet. Save a custom report from the chat to re-run it later.";

/**
 * Only a CUSTOM report can be saved.
 *
 * The three native report types are already reusable — they are named reports you
 * can ask for again — and have no stored pipeline to extract, which is why the
 * backend refuses them. Offering the control and then surfacing that refusal
 * would be a worse experience than not offering it.
 *
 * A report restored from chat history carries only its id, not its type, so the
 * control appears on freshly generated reports. That matches the story ("on a
 * generated CUSTOM report") rather than working around it.
 */
export const canSaveAsDefinition = (
	report?: AnalyticsGeneratedReport,
): boolean => report?.reportType === "CUSTOM";

/** Seed name for the save dialog: the report's own title, else its file name. */
export const defaultDefinitionName = (
	report?: AnalyticsGeneratedReport,
): string => {
	const title = report?.preview?.title?.trim();
	if (title) return title;
	const fileName = report?.fileName ?? "";
	const stem = fileName.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ");
	return stem.trim() || "Saved report";
};

/** One-line summary of what a re-run will ask for. */
export const describeParameters = (
	definition: AnalyticsReportDefinition,
): string => {
	const parameters = definition.parameters ?? [];
	if (parameters.length === 0) return "No parameters";
	return parameters.map((p) => p.label).join(" · ");
};

/** Slot values pre-filled from the definition's own defaults, as form strings. */
export const initialParamValues = (
	parameters: AnalyticsDefinitionParam[] = [],
): Record<string, string> => {
	const values: Record<string, string> = {};
	for (const parameter of parameters) {
		values[parameter.name] =
			parameter.defaultValue === undefined || parameter.defaultValue === null
				? ""
				: String(parameter.defaultValue);
	}
	return values;
};

/** Helper text under a slot field, so the expected shape is stated up front. */
export const describeParamType = (
	parameter: AnalyticsDefinitionParam,
): string => {
	if (parameter.type === "OBJECT_ID") return "24-character id";
	if (parameter.type === "DATE") return "ISO date, e.g. 2026-07-01";
	if (parameter.type === "NUMBER") return "Number";
	return "Text";
};

export interface ParamFieldError {
	name: string;
	message: string;
}

/**
 * Client-side pre-check of the values a user typed.
 *
 * The server type-checks every value before it reaches the pipeline and remains
 * the authority; this only avoids a round trip for a mistake that is obvious on
 * the form, and it mirrors the server's shapes rather than inventing looser ones.
 */
export const validateParamValues = (
	parameters: AnalyticsDefinitionParam[] = [],
	values: Record<string, string> = {},
): ParamFieldError[] => {
	const errors: ParamFieldError[] = [];
	for (const parameter of parameters) {
		const raw = (values[parameter.name] ?? "").trim();
		if (!raw) {
			errors.push({
				name: parameter.name,
				message: `${parameter.label} is required to run this report.`,
			});
			continue;
		}
		if (parameter.type === "OBJECT_ID" && !OBJECT_ID_PATTERN.test(raw)) {
			errors.push({
				name: parameter.name,
				message: `${parameter.label} must be a 24-character hex id.`,
			});
			continue;
		}
		if (
			parameter.type === "DATE" &&
			(!ISO_DATE_PATTERN.test(raw) || Number.isNaN(new Date(raw).getTime()))
		) {
			errors.push({
				name: parameter.name,
				message: `${parameter.label} must be an ISO-8601 date, e.g. 2026-07-01.`,
			});
			continue;
		}
		if (parameter.type === "NUMBER" && !Number.isFinite(Number(raw))) {
			errors.push({
				name: parameter.name,
				message: `${parameter.label} must be a number.`,
			});
		}
	}
	return errors;
};

/**
 * Form strings to the run payload, keyed by declared slot name only.
 *
 * A key the definition does not declare is dropped here as well as ignored
 * server-side, so nothing the form holds can become a filter the definition never
 * declared.
 */
export const toRunValues = (
	parameters: AnalyticsDefinitionParam[] = [],
	values: Record<string, string> = {},
): Record<string, string | number> => {
	const payload: Record<string, string | number> = {};
	for (const parameter of parameters) {
		const raw = (values[parameter.name] ?? "").trim();
		if (!raw) continue;
		payload[parameter.name] = parameter.type === "NUMBER" ? Number(raw) : raw;
	}
	return payload;
};

/**
 * The server's own explanation, if it sent one.
 *
 * Two body shapes are possible: Nest's `{ statusCode, message, error }` for an
 * HttpException, and `BaseResponse.buildError`'s `{ error: { errorMessage } }`
 * for anything unhandled. `message` is an array when the validation pipe rejected
 * the payload.
 */
export const readServerMessage = (err: unknown): string | undefined => {
	if (!err || typeof err !== "object") return undefined;
	// An Error carries a technical `message` ("Network Error", "socket hang up").
	// A server explanation always arrives as a response body, so an Error means
	// the server never got to explain and the caller should say so in its own
	// words rather than show transport wording to a user.
	if (err instanceof Error) return undefined;
	const body = err as {
		message?: unknown;
		errorMessage?: unknown;
		error?: { errorMessage?: unknown };
	};
	const candidate = Array.isArray(body.message)
		? body.message.filter((m) => typeof m === "string").join(" ")
		: body.message;
	for (const value of [
		candidate,
		body.error?.errorMessage,
		body.errorMessage,
	]) {
		if (typeof value === "string" && value.trim()) return value.trim();
	}
	return undefined;
};

/**
 * Why a save was refused.
 *
 * The interesting case is the identifying-field refusal: the backend will not
 * store a pipeline that filters or groups on a field identifying an individual,
 * because a one-beneficiary query is not a reusable pattern. That message already
 * names the offending field and says what to do instead, so it is shown verbatim.
 * Collapsing it into "something went wrong" would hide the only part a user can
 * act on — which is the whole reason this returns the server's text rather than
 * its own.
 */
export const describeSaveFailure = (err: unknown): string =>
	readServerMessage(err) ??
	"Could not save this report. Please try again, or generate the report once more and save from there.";

export type RunFailureKind =
	| "PARAMETER"
	| "DEFINITION"
	| "NOT_FOUND"
	| "UNKNOWN";

export interface RunFailure {
	kind: RunFailureKind;
	/** Short heading naming what went wrong. */
	heading: string;
	/** The server's own words, when it sent any. */
	detail: string;
	/**
	 * Why this can happen to a report that used to work. Present only where it is
	 * true — a mistyped parameter is not a stale definition.
	 */
	explanation?: string;
}

/**
 * Classify a failed re-run so it reads as an explanation rather than a crash.
 *
 * A saved definition re-runs through the full security envelope on every run —
 * allow-listed collection, stage and operator checks, the account scope prepend,
 * the beneficiary-name gate, the cost pre-flight. That is what makes a definition
 * safe to keep, and it is also why one saved months ago can legitimately stop
 * validating: the schema, the allow-list or the account's privacy settings may
 * have moved under it. That outcome is expected, so it gets a specific
 * explanation.
 *
 * Classification is on the message text rather than the status code, because the
 * service rejects with the response body and a `statusCode` is not guaranteed to
 * be in it.
 */
export const classifyRunFailure = (
	err: unknown,
	definitionName: string,
): RunFailure => {
	const detail = readServerMessage(err);
	const quotedName = `“${definitionName}”`;

	if (!detail) {
		return {
			kind: "UNKNOWN",
			heading: `Could not run ${quotedName}.`,
			detail: "The assistant did not say why. Please try again in a moment.",
		};
	}

	// The server prefixes every per-slot rejection with `Parameter "<slot>"`, so a
	// value the user can simply correct is never dressed up as a stale definition.
	if (/^Parameter\s+"/.test(detail)) {
		return {
			kind: "PARAMETER",
			heading: "Check the values below.",
			detail,
		};
	}

	if (/not found/i.test(detail)) {
		return {
			kind: "NOT_FOUND",
			heading: `${quotedName} is no longer available.`,
			detail,
		};
	}

	return {
		kind: "DEFINITION",
		heading: `${quotedName} could not be run against the current data.`,
		detail,
		explanation:
			"A saved report is re-checked against the current data model and your account's privacy settings on every run, so one that worked before can stop validating. Generate the report again from the chat and save it anew.",
	};
};
